import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa6";
import useChatbotEngine, { ORDER_REFERENCE_PATTERN } from "../hooks/useChatbotEngine";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import { getWhatsAppMessage, getWhatsAppLink } from "../data/whatsappContext";
import { CHATBOT_GREETING, CHATBOT_NAME, EXAMPLE_QUESTIONS } from "../data/chatbotContent";
import { trackEvent } from "../utils/analytics";

interface ChatMessage {
  id: number;
  role: "bot" | "user";
  text: string;
  linkPath?: string;
  linkLabel?: string;
  showWhatsAppFallback?: boolean;
}

/**
 * Panel and launcher only — fixed positioning lives in the parent
 * EngagementWidgetStack, alongside WhatsAppWidget. Not a page-blocking
 * modal (background stays interactive, no aria-modal) — a persistent
 * disclosure widget, matching how real chat widgets behave, distinct from
 * this project's full-screen dialog pattern (SearchModal.tsx etc).
 */
export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: 0, role: "bot", text: CHATBOT_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const nextId = useRef(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const { ask, lookupOrderStatus } = useChatbotEngine();

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: reducedMotion ? "auto" : "smooth" });
  }, [messages, typing, reducedMotion]);

  const showAnswer = (answer: ReturnType<typeof ask>) => {
    setMessages((prev) => [
      ...prev,
      {
        id: nextId.current++,
        role: "bot",
        text: answer.confident
          ? answer.text
          : `${answer.text} Want me to connect you with a real person instead?`,
        linkPath: answer.confident ? answer.linkPath : undefined,
        linkLabel: answer.confident ? answer.linkLabel : undefined,
        showWhatsAppFallback: !answer.confident,
      },
    ]);
    setTyping(false);
    trackEvent("chatbot_answer_shown", { confident: answer.confident });
  };

  const respond = (question: string) => {
    const userMsg: ChatMessage = { id: nextId.current++, role: "user", text: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    trackEvent("chatbot_question_asked");

    // An order-reference lookup is a genuine Firestore round-trip (see
    // useChatbotEngine.ts's lookupOrderStatus) — awaited for real, not
    // padded with the artificial delay below, which would be dishonest on
    // top of already-real latency.
    const orderRefMatch = question.match(ORDER_REFERENCE_PATTERN);
    if (orderRefMatch) {
      // getOrderByReference never throws (see firebaseClient.ts), but this
      // catch is a deliberate second line of defense — without it, any
      // unexpected rejection here would leave setTyping(false) (only ever
      // called inside showAnswer) unreached, stranding the "…" indicator
      // on-screen forever with no error surfaced.
      lookupOrderStatus(orderRefMatch[0])
        .then(showAnswer)
        .catch(() =>
          showAnswer({ confident: false, text: "Something went wrong looking up that order — please try again." })
        );
      return;
    }

    // A brief, honest pause before replying — this is instant local
    // matching (see useChatbotEngine.ts), not a real network round-trip, so
    // it stays far under the AC's 3s latency budget; the pause is purely a
    // UX courtesy so a reply doesn't feel like it appeared before the
    // question finished rendering.
    setTimeout(() => {
      showAnswer(ask(question));
    }, 400);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    respond(input);
  };

  const whatsappHref = getWhatsAppLink(getWhatsAppMessage("/"));

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label={CHATBOT_NAME}
            initial={reducedMotion ? false : { opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: reducedMotion ? 0 : 0.2 }}
            className="mb-2 flex h-[28rem] w-[min(22rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-ocean-900/10 dark:bg-ocean-900 dark:ring-white/10"
          >
            <div className="flex items-center justify-between bg-ocean-950 px-4 py-3 text-foam-50">
              <span className="text-sm font-semibold">{CHATBOT_NAME}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="rounded-full p-1 hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-teal-400 text-ocean-950"
                        : "bg-foam-100 text-ocean-900 dark:bg-white/10 dark:text-foam-50"
                    }`}
                  >
                    <p>{m.text}</p>
                    {m.linkPath && (
                      <Link
                        to={m.linkPath}
                        onClick={() => setOpen(false)}
                        className="mt-1.5 inline-block text-xs font-semibold text-teal-700 hover:text-teal-800"
                      >
                        {m.linkLabel} →
                      </Link>
                    )}
                    {m.showWhatsAppFallback && (
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-800"
                      >
                        <FaWhatsapp className="h-3.5 w-3.5" />
                        Chat on WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-foam-100 px-3.5 py-2 text-sm text-ocean-800/50 dark:bg-white/10 dark:text-foam-100/50">…</div>
                </div>
              )}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {EXAMPLE_QUESTIONS.map((eq) => (
                    <button
                      key={eq}
                      type="button"
                      onClick={() => respond(eq)}
                      className="rounded-full bg-ocean-900/5 px-3 py-1.5 text-xs font-medium text-ocean-800 hover:bg-ocean-900/10 dark:bg-white/10 dark:text-foam-100 dark:hover:bg-white/15"
                    >
                      {eq}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t border-ocean-900/5 p-3 dark:border-white/10">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question…"
                aria-label="Your question"
                className="flex-1 rounded-full border border-ocean-900/10 bg-foam-50 px-4 py-2 text-sm text-ocean-950 outline-none focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={!input.trim()}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-400 text-ocean-950 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close ZenFresh Assistant" : "Open ZenFresh Assistant"}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-ocean-950 text-foam-50 shadow-lg shadow-ocean-950/20 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
}
